import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { SortHeader } from '@/components/ui/sort-header'
import { useSortable } from '@/lib/useSortable'
import { MONO, fmt, fmtUnits } from './format'

const CARD = 'rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900'

/** A clinic's paid-vs-included totals for the active range (windowed scalars). */
type IClient = { clinic: string; group: string; cur: string; paidUnits: number; includedUnits: number; totalUnits: number; paidValue: number }

const inclPct = (c: IClient) => (c.totalUnits ? c.includedUnits / c.totalUnits : 0)
const valueOf = (c: IClient, key: string): number | string => {
  switch (key) {
    case 'clinic': return c.clinic
    case 'group': return c.group
    case 'paidUnits': return c.paidUnits
    case 'includedUnits': return c.includedUnits
    case 'inclPct': return inclPct(c)
    case 'paidValue': return c.paidValue
    default: return 0
  }
}

/** Inclusive-contract clinics and what they spend (paid vs included). Every
 *  column is click-to-sort (default: Paid value ↓). `clients` is already
 *  filtered/converted and windowed to the range. */
export function IncludedClientsCard({ clients, currency }: { clients: IClient[]; currency: string }) {
  const { sorted: rows, sortKey, sortDir, toggle } = useSortable(clients, valueOf, { key: 'paidValue', dir: 'desc' })
  const tot = rows.reduce(
    (a, c) => ({ paid: a.paid + c.paidUnits, incl: a.incl + c.includedUnits, total: a.total + c.totalUnits, value: a.value + c.paidValue }),
    { paid: 0, incl: 0, total: 0, value: 0 },
  )

  return (
    <div className={CARD}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-5">
        <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
          Included-contract clients
          <InfoPopover term="included" />
        </h3>
        <span className="text-[11px] text-stone-400 dark:text-stone-500">{rows.length} clinics on inclusive-fee groups · paid vs included</span>
      </div>

      <div className="mt-3 overflow-auto" style={{ maxHeight: 480 }}>
        <table className="w-full min-w-[600px] border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-white dark:bg-stone-900">
            <tr className="border-y border-stone-100 text-xs text-stone-400 dark:border-stone-800 dark:text-stone-500">
              <th className="sticky left-0 z-20 bg-white px-5 py-2 text-left font-medium dark:bg-stone-900">
                <SortHeader label="Clinic" sortKey="clinic" active={sortKey === 'clinic'} dir={sortDir} onSort={toggle} align="left" />
              </th>
              <th className="px-3 py-2 text-left font-medium">
                <SortHeader label="Group" sortKey="group" active={sortKey === 'group'} dir={sortDir} onSort={toggle} align="left" />
              </th>
              <th className="px-3 py-2 text-right font-medium">
                <SortHeader label="Paid u." sortKey="paidUnits" active={sortKey === 'paidUnits'} dir={sortDir} onSort={toggle} />
              </th>
              <th className="px-3 py-2 text-right font-medium">
                <SortHeader label="Incl. u." sortKey="includedUnits" active={sortKey === 'includedUnits'} dir={sortDir} onSort={toggle} />
              </th>
              <th className="px-3 py-2 text-right font-medium">
                <SortHeader label="Incl. %" sortKey="inclPct" active={sortKey === 'inclPct'} dir={sortDir} onSort={toggle} />
              </th>
              <th className="px-5 py-2 text-right font-semibold text-stone-600 dark:text-stone-300">
                <SortHeader label="Paid value" sortKey="paidValue" active={sortKey === 'paidValue'} dir={sortDir} onSort={toggle} />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.clinic} className="border-b border-stone-50 even:bg-stone-50/40 dark:border-stone-800/60 dark:even:bg-stone-800/20">
                <td className="sticky left-0 z-10 max-w-[200px] truncate bg-white px-5 py-2 text-stone-700 dark:bg-stone-900 dark:text-stone-200" title={c.clinic}>
                  {c.clinic}
                </td>
                <td className="px-3 py-2 text-stone-500 dark:text-stone-400">
                  <span className="whitespace-nowrap rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium dark:bg-stone-800">{c.group}</span>
                </td>
                <td className={cn('px-3 py-2 text-right text-stone-500 dark:text-stone-400', MONO)}>{fmtUnits(c.paidUnits)}</td>
                <td className={cn('px-3 py-2 text-right text-stone-500 dark:text-stone-400', MONO)}>{fmtUnits(c.includedUnits)}</td>
                <td className={cn('px-3 py-2 text-right text-stone-500 dark:text-stone-400', MONO)}>{c.totalUnits ? Math.round((c.includedUnits / c.totalUnits) * 100) : 0}%</td>
                <td className={cn('px-5 py-2 text-right font-semibold', MONO)}>{c.paidValue ? fmt(c.paidValue, currency) : '—'}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-stone-200 dark:border-stone-700">
              <td className="sticky left-0 z-10 bg-white px-5 py-2.5 font-semibold dark:bg-stone-900">Total</td>
              <td />
              <td className={cn('px-3 py-2.5 text-right font-bold', MONO)}>{fmtUnits(tot.paid)}</td>
              <td className={cn('px-3 py-2.5 text-right font-bold', MONO)}>{fmtUnits(tot.incl)}</td>
              <td className={cn('px-3 py-2.5 text-right font-bold', MONO)}>{tot.total ? Math.round((tot.incl / tot.total) * 100) : 0}%</td>
              <td className={cn('px-5 py-2.5 text-right font-bold', MONO)}>{fmt(tot.value, currency)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
